import {
  getApiNotificationsV1PreferencesRetrieveQueryKey,
  type SimpleUserNotificationPreference,
  type UserNotificationPreferenceUpdate,
  useApiNotificationsV1PreferencesUpdate,
  useQueryClient,
} from 'api';

export const getOptimisticNotificationPreference = ({
  current,
  data,
}: {
  current: SimpleUserNotificationPreference | undefined;
  data: UserNotificationPreferenceUpdate;
}): SimpleUserNotificationPreference => {
  const nextEmailEnabled = data.email_enabled ?? current?.email_enabled ?? true;
  const nextQualityDigestEnabled = data.quality_digest_enabled ?? current?.quality_digest_enabled ?? false;

  return {
    email_enabled: nextEmailEnabled,
    quality_digest_enabled: nextEmailEnabled && nextQualityDigestEnabled,
  };
};

export const useUpdateNotificationPreference = () => {
  const queryClient = useQueryClient();
  const notificationPreferenceQueryKey = getApiNotificationsV1PreferencesRetrieveQueryKey();
  const { mutate: updateNotificationPreference } = useApiNotificationsV1PreferencesUpdate({
    mutation: {
      onError: (_error, _variables, previousNotificationPreference) => {
        if (previousNotificationPreference) {
          queryClient.setQueryData(notificationPreferenceQueryKey, previousNotificationPreference);
        }
      },
      onMutate: async ({ data }) => {
        await queryClient.cancelQueries({
          queryKey: notificationPreferenceQueryKey,
        });
        const previousNotificationPreference =
          queryClient.getQueryData<SimpleUserNotificationPreference>(notificationPreferenceQueryKey);

        queryClient.setQueryData<SimpleUserNotificationPreference>(notificationPreferenceQueryKey, (current) =>
          getOptimisticNotificationPreference({ current, data })
        );

        return previousNotificationPreference;
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: notificationPreferenceQueryKey,
        });
      },
    },
  });

  return updateNotificationPreference;
};
